import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  ALL_MODERATOR_PERMISSIONS,
  PLATFORM_TYPE_ENUM,
  USER_ROLES_ENUM,
} from "@/constants/permissions";
import type { OperationalRoles, Staff } from "@/types";
import type { RootState } from "..";

/**
 * The `operationalRoles` slice and the permission selector — the original's
 * module **`5342`** (`CG` = fetchOperationalRoles, `dA` = reset, `Al` = the
 * `hasPermission` selector).
 *
 * The matrix is a `{ [ROLE]: permission[] }` map fetched per tenant. It is
 * **not** persisted: a role matrix restored from `localStorage` would let a
 * revoked permission survive its revocation until the next fetch, and the
 * fetch is one request on boot.
 */

export interface OperationalRolesState {
  data: OperationalRoles | null;
  loading: boolean;
}

const initialState: OperationalRolesState = { data: null, loading: false };

const operationalRolesSlice = createSlice({
  name: "operational-roles",
  initialState,
  reducers: {
    setOperationalRoles(state, action: PayloadAction<OperationalRoles | null>) {
      state.data = action.payload ?? null;
      state.loading = false;
    },
    setOperationalRolesLoading(state, action: PayloadAction<boolean>) {
      state.loading = Boolean(action.payload);
    },
    /** `dA` — dropped on logout and whenever staff mode ends. */
    resetOperationalRoles() {
      return initialState;
    },
  },
});

export const {
  setOperationalRoles,
  setOperationalRolesLoading,
  resetOperationalRoles,
} = operationalRolesSlice.actions;
export default operationalRolesSlice.reducer;

/* -------------------------------------------------------------------------- */
/* the permission test                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Module `5342`'s `_`, verbatim:
 *
 * ```js
 * function _(e, t, r) {                      // staff, roles, permission
 *   if (!e) return !1;
 *   let i = e.role;
 *   return !1 !== r && !!i && !!t && !!t[i] && !e.is_disabled &&
 *     (e.allowed_platforms?.includes(WEB)) !== !1 &&
 *     (!0 === r || i === SUPER_ADMIN ||
 *      (i === MODERATOR ? ALL_MODERATOR_PERMISSIONS.includes(r) : t[i].includes(r)));
 * }
 * ```
 *
 * Four things in there are easy to get wrong and all of them matter:
 *
 * 1. **`t[i]` must exist.** A role the tenant's matrix does not mention has no
 *    permissions at all, even for `SUPER_ADMIN` — the `!!t[i]` guard runs
 *    before the short-circuit.
 * 2. **`allowed_platforms?.includes(WEB) !== false`** is a THREE-state test, not
 *    a boolean. An absent list passes (`undefined !== false`); a list that
 *    omits `WEB` fails. A staff member who may use the till but not the
 *    storefront therefore has no storefront permission.
 * 3. **`r === true`** is a legitimate argument meaning "any permission will do",
 *    and `r === false` short-circuits to `false`.
 * 4. **`MODERATOR` is checked against a FIXED list**, not the tenant's matrix —
 *    and that list contains none of the six permissions the storefront reads.
 */
export const staffHasPermission = (
  staff: Staff | null | undefined,
  roles: Record<string, string[]> | null | undefined,
  permission: string | boolean,
): boolean => {
  if (!staff) return false;
  const role = staff.role;
  if (permission === false) return false;
  if (!role || !roles || !roles[role]) return false;
  if (staff.is_disabled) return false;
  if (staff.allowed_platforms?.includes(PLATFORM_TYPE_ENUM.WEB) === false) {
    return false;
  }
  if (permission === true) return true;
  if (role === USER_ROLES_ENUM.SUPER_ADMIN) return true;
  if (role === USER_ROLES_ENUM.MODERATOR) {
    return ALL_MODERATOR_PERMISSIONS.includes(permission);
  }
  return roles[role].includes(permission);
};

/**
 * `Al` — the selector form. Read as
 * `useAppSelector((s) => hasPermission(s, USER_PERMISSIONS_ENUM.POS_VIEW_REPORTS))`.
 */
export const hasPermission = (
  state: RootState,
  permission: string | boolean,
): boolean =>
  staffHasPermission(
    state.auth.staff,
    state.operationalRoles.data?.roles,
    permission,
  );
